/**
 * Printed QR codes: the path they encode, the file they are saved as, and the
 * encoder that draws them.
 *
 * Everything here is pure and imports NOTHING - not even zod - so it is exposed
 * as the bare subpath `@croissants/contracts/qr` and every surface that has to
 * agree about a printed code can reach it: the worker's sheet builder, the guest
 * app's URL parser, and both admin portals' pairing dialogs.
 */

/**
 * The URL path a printed QR sheet encodes its token on: the payload is
 * `<guest origin>/q/<token>` (OPS-019, OPS-020).
 *
 * Declared HERE - in the one package both sides already depend on - because the
 * surface that PRINTS the code (the services `qr-batch` sheet builder) and the
 * surface that RECEIVES the scan (the guest app's URL parser) once each held
 * their own copy of this string, drifted, and every printed sheet became a URL
 * the guest app did not recognise (TST-008). Printed codes are screwed to
 * apartment walls and cannot be recalled, so the two sides may never disagree
 * again: both must import this constant, and neither may write '/q/' as a
 * literal.
 */
export const PRINTED_QR_PATH = '/q/';

/**
 * The guest app's "how to reach us" page, which the SMALL code on the card's
 * reverse opens (WEB-021, GST-043). Shared for the reason above: the card
 * prints it, the guest app routes it, and a printed card cannot be recalled.
 * The page reads what it offers from the platform at the moment it opens, so
 * a number added after the cards were printed reaches every card already out.
 */
export const SUPPORT_PAGE_PATH = '/support';

/**
 * The file name a QR sheet is saved under: `CroissantInBed_<apartment>.pdf`
 * for one apartment, `CroissantInBed_<n> apartments.pdf` for a selection
 * (founder decision 2026-08-22). Declared here, beside the path the sheet
 * encodes, because the worker names the file in its `Content-Disposition` and
 * the partner app names the download it falls back to - and a name written
 * twice is a name that drifts.
 *
 * The apartment reference is the partner's own words, so only what a file
 * system refuses is dropped; accents and spaces stay.
 */
export function qrSheetFileName(references: readonly string[]): string {
  const stem =
    references.length === 1
      ? [...(references[0] ?? '')]
          .filter((ch) => !'\\/:*?"<>|'.includes(ch) && (ch.codePointAt(0) ?? 0) >= 0x20)
          .join('')
          .trim() || 'QR'
      : `${references.length} apartments`;
  return `CroissantInBed_${stem}.pdf`;
}

// ---------------------------------------------------------------------------
// The encoder (WEB-019, WEB-022; tech-stack section 2: QR is a hand-written
// encoder, because no QR package passes the dependency budget)
// ---------------------------------------------------------------------------

/**
 * Scope, deliberately fixed: BYTE mode, error-correction level M, versions
 * 1-10 -- more than enough for a guest-storefront URL, and the smallest
 * surface that is fully verifiable. Implements ISO/IEC 18004: data encoding,
 * Reed-Solomon over GF(256), block interleaving, matrix construction with
 * alignment patterns, version information (v7+), all eight masks with the
 * standard penalty selection, and the BCH-protected format information.
 *
 * It lives HERE rather than in `packages/services` for the same reason the path
 * above does: both sides of a printed code need it. The server draws the sheet,
 * and the two admin portals draw the pairing QR on screen and on paper - and
 * `scripts/check-import-graph.mjs` rule (c) forbids a frontend importing
 * `@croissants/services`, which is a server-only layer. A second encoder in the
 * browser would be a second answer to "what does this code say", on artwork
 * somebody screws to a wall.
 */

/**
 * UTF-8 bytes, in pure TS. Written out rather than reached for through
 * `TextEncoder` because this module compiles under bare ES2023 - no DOM lib, no
 * Node types - and is the one module in the package that imports nothing.
 */
function utf8Bytes(text: string): Uint8Array {
  const out: number[] = [];
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x80) out.push(code);
    else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    }
  }
  return Uint8Array.from(out);
}

// ---------------------------------------------------------------------------
// GF(256) arithmetic (polynomial 0x11d), for Reed-Solomon
// ---------------------------------------------------------------------------

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) GF_EXP[i] = GF_EXP[i - 255] ?? 0;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[((GF_LOG[a] ?? 0) + (GF_LOG[b] ?? 0)) % 255] ?? 0;
}

/** Generator polynomial of degree `n` (product of (x - alpha^i), i = 0..n-1). */
function rsGenerator(n: number): number[] {
  let poly = [1];
  for (let i = 0; i < n; i += 1) {
    poly = multiplyByMonomial(poly, GF_EXP[i] ?? 0);
  }
  return poly;
}

/** poly * (x + a) in GF(256), coefficients highest-degree first. */
function multiplyByMonomial(poly: readonly number[], a: number): number[] {
  const result = new Array<number>(poly.length + 1).fill(0);
  for (let i = 0; i < poly.length; i += 1) {
    result[i] = (result[i] ?? 0) ^ (poly[i] ?? 0); // x * poly
    result[i + 1] = (result[i + 1] ?? 0) ^ gfMul(poly[i] ?? 0, a); // a * poly
  }
  return result;
}

/** Reed-Solomon EC codewords for `data`, `ecCount` of them. */
export function rsEncode(data: readonly number[], ecCount: number): number[] {
  const generator = rsGenerator(ecCount);
  const remainder = new Array<number>(ecCount).fill(0);
  for (const byte of data) {
    const factor = (byte ?? 0) ^ (remainder[0] ?? 0);
    remainder.shift();
    remainder.push(0);
    for (let i = 0; i < generator.length - 1; i += 1) {
      remainder[i] = (remainder[i] ?? 0) ^ gfMul(generator[i + 1] ?? 0, factor);
    }
  }
  return remainder;
}

// ---------------------------------------------------------------------------
// Version tables -- EC level M, versions 1..10 (ISO/IEC 18004 table 9)
// ---------------------------------------------------------------------------

interface BlockGroup {
  readonly blocks: number;
  readonly dataCodewords: number;
}

interface VersionSpec {
  readonly version: number;
  readonly ecPerBlock: number;
  readonly groups: readonly BlockGroup[];
}

const EC_M_SPECS: readonly VersionSpec[] = [
  { version: 1, ecPerBlock: 10, groups: [{ blocks: 1, dataCodewords: 16 }] },
  { version: 2, ecPerBlock: 16, groups: [{ blocks: 1, dataCodewords: 28 }] },
  { version: 3, ecPerBlock: 26, groups: [{ blocks: 1, dataCodewords: 44 }] },
  { version: 4, ecPerBlock: 18, groups: [{ blocks: 2, dataCodewords: 32 }] },
  { version: 5, ecPerBlock: 24, groups: [{ blocks: 2, dataCodewords: 43 }] },
  { version: 6, ecPerBlock: 16, groups: [{ blocks: 4, dataCodewords: 27 }] },
  { version: 7, ecPerBlock: 18, groups: [{ blocks: 4, dataCodewords: 31 }] },
  {
    version: 8,
    ecPerBlock: 22,
    groups: [
      { blocks: 2, dataCodewords: 38 },
      { blocks: 2, dataCodewords: 39 },
    ],
  },
  {
    version: 9,
    ecPerBlock: 22,
    groups: [
      { blocks: 3, dataCodewords: 36 },
      { blocks: 2, dataCodewords: 37 },
    ],
  },
  {
    version: 10,
    ecPerBlock: 26,
    groups: [
      { blocks: 4, dataCodewords: 43 },
      { blocks: 1, dataCodewords: 44 },
    ],
  },
];

/** Alignment-pattern center coordinates per version (table E.1). */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [], // v1
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
];

function totalDataCodewords(spec: VersionSpec): number {
  return spec.groups.reduce((n, g) => n + g.blocks * g.dataCodewords, 0);
}

/** Byte-mode capacity in bytes at EC M for a version. */
export function byteCapacity(version: number): number {
  const spec = EC_M_SPECS[version - 1];
  if (spec === undefined) throw new RangeError(`Unsupported version ${version}`);
  const countBits = version <= 9 ? 8 : 16;
  return Math.floor((totalDataCodewords(spec) * 8 - 4 - countBits) / 8);
}

// ---------------------------------------------------------------------------
// Bit buffer + data codewords
// ---------------------------------------------------------------------------

class BitBuffer {
  readonly bits: number[] = [];

  push(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i -= 1) this.bits.push((value >> i) & 1);
  }

  toBytes(): number[] {
    const bytes: number[] = [];
    for (let i = 0; i < this.bits.length; i += 8) {
      let byte = 0;
      for (let j = 0; j < 8; j += 1) byte = (byte << 1) | (this.bits[i + j] ?? 0);
      bytes.push(byte);
    }
    return bytes;
  }
}

/** Data codewords for `payload` at `version` (byte mode, EC M): header, data,
 * terminator, byte alignment, then EC/11 pad bytes. */
export function dataCodewords(payload: Uint8Array, version: number): number[] {
  const spec = EC_M_SPECS[version - 1];
  if (spec === undefined) throw new RangeError(`Unsupported version ${version}`);
  const capacityBits = totalDataCodewords(spec) * 8;
  const countBits = version <= 9 ? 8 : 16;

  const buffer = new BitBuffer();
  buffer.push(0b0100, 4); // byte mode
  buffer.push(payload.length, countBits);
  for (const byte of payload) buffer.push(byte, 8);
  // Terminator: up to four zero bits.
  const terminator = Math.min(4, capacityBits - buffer.bits.length);
  buffer.push(0, terminator);
  // Pad to a byte boundary.
  while (buffer.bits.length % 8 !== 0) buffer.bits.push(0);
  // Alternating pad codewords.
  const bytes = buffer.toBytes();
  const pads = [0xec, 0x11];
  let padIndex = 0;
  while (bytes.length < totalDataCodewords(spec)) {
    bytes.push(pads[padIndex % 2] ?? 0xec);
    padIndex += 1;
  }
  return bytes;
}

/** Splits data into blocks, computes EC, and interleaves both (section 8.6). */
export function interleave(codewords: readonly number[], version: number): number[] {
  const spec = EC_M_SPECS[version - 1];
  if (spec === undefined) throw new RangeError(`Unsupported version ${version}`);

  const dataBlocks: number[][] = [];
  let offset = 0;
  for (const group of spec.groups) {
    for (let b = 0; b < group.blocks; b += 1) {
      dataBlocks.push([...codewords.slice(offset, offset + group.dataCodewords)]);
      offset += group.dataCodewords;
    }
  }
  const ecBlocks = dataBlocks.map((block) => rsEncode(block, spec.ecPerBlock));

  const out: number[] = [];
  const maxData = Math.max(...dataBlocks.map((b) => b.length));
  for (let i = 0; i < maxData; i += 1) {
    for (const block of dataBlocks) {
      const value = block[i];
      if (value !== undefined) out.push(value);
    }
  }
  for (let i = 0; i < spec.ecPerBlock; i += 1) {
    for (const block of ecBlocks) {
      const value = block[i];
      if (value !== undefined) out.push(value);
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Format and version information
// ---------------------------------------------------------------------------

/** 15-bit format info: EC level M ('00') + mask, BCH(15,5), XOR 0x5412. */
export function formatInfoBits(maskPattern: number): number {
  const data = (0b00 << 3) | maskPattern; // M = 00
  let value = data << 10;
  const generator = 0b10100110111; // 0x537
  for (let i = 14; i >= 10; i -= 1) {
    if (value & (1 << i)) value ^= generator << (i - 10);
  }
  return ((data << 10) | value) ^ 0b101010000010010; // XOR mask 0x5412
}

/** 18-bit version info for versions >= 7: BCH(18,6), generator 0x1F25. */
export function versionInfoBits(version: number): number {
  let value = version << 12;
  const generator = 0b1111100100101; // 0x1F25
  for (let i = 17; i >= 12; i -= 1) {
    if (value & (1 << i)) value ^= generator << (i - 12);
  }
  return (version << 12) | value;
}

// ---------------------------------------------------------------------------
// Matrix construction
// ---------------------------------------------------------------------------

export interface QrMatrix {
  readonly version: number;
  readonly size: number;
  /** modules[row][col] -- true is dark. */
  readonly modules: readonly (readonly boolean[])[];
  readonly maskPattern: number;
}

interface Grid {
  readonly size: number;
  readonly modules: boolean[][];
  readonly isFunction: boolean[][];
}

function emptyGrid(size: number): Grid {
  return {
    size,
    modules: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
    isFunction: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  };
}

function setModule(grid: Grid, row: number, col: number, dark: boolean): void {
  const moduleRow = grid.modules[row];
  const functionRow = grid.isFunction[row];
  if (moduleRow === undefined || functionRow === undefined) return;
  moduleRow[col] = dark;
  functionRow[col] = true;
}

function placeFinder(grid: Grid, top: number, left: number): void {
  for (let r = -1; r <= 7; r += 1) {
    for (let c = -1; c <= 7; c += 1) {
      const row = top + r;
      const col = left + c;
      if (row < 0 || row >= grid.size || col < 0 || col >= grid.size) continue;
      const inRing =
        r >= 0 && r <= 6 && c >= 0 && c <= 6 && (r === 0 || r === 6 || c === 0 || c === 6);
      const inCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      setModule(grid, row, col, inRing || inCenter);
    }
  }
}

function placeAlignment(grid: Grid, centerRow: number, centerCol: number): void {
  for (let r = -2; r <= 2; r += 1) {
    for (let c = -2; c <= 2; c += 1) {
      const dark = Math.max(Math.abs(r), Math.abs(c)) !== 1;
      setModule(grid, centerRow + r, centerCol + c, dark);
    }
  }
}

function placeFunctionPatterns(grid: Grid, version: number): void {
  const size = grid.size;
  placeFinder(grid, 0, 0);
  placeFinder(grid, 0, size - 7);
  placeFinder(grid, size - 7, 0);

  // Timing patterns.
  for (let i = 8; i < size - 8; i += 1) {
    if (grid.isFunction[6]?.[i] !== true) setModule(grid, 6, i, i % 2 === 0);
    if (grid.isFunction[i]?.[6] !== true) setModule(grid, i, 6, i % 2 === 0);
  }

  // Alignment patterns (skip any overlapping a finder).
  const centers = ALIGNMENT_CENTERS[version - 1] ?? [];
  for (const row of centers) {
    for (const col of centers) {
      const nearFinder =
        (row <= 8 && col <= 8) || (row <= 8 && col >= size - 9) || (row >= size - 9 && col <= 8);
      if (!nearFinder) placeAlignment(grid, row, col);
    }
  }

  // Dark module.
  setModule(grid, size - 8, 8, true);

  // Reserve format-information areas (filled after masking).
  for (let i = 0; i <= 8; i += 1) {
    if (i !== 6) {
      reserve(grid, 8, i);
      reserve(grid, i, 8);
    }
  }
  for (let i = 0; i < 8; i += 1) {
    reserve(grid, 8, size - 1 - i);
    reserve(grid, size - 1 - i, 8);
  }

  // Version information (v7+): two 6x3 blocks.
  if (version >= 7) {
    const bits = versionInfoBits(version);
    for (let i = 0; i < 18; i += 1) {
      const bit = ((bits >> i) & 1) === 1;
      const a = Math.floor(i / 3);
      const b = size - 11 + (i % 3);
      setModule(grid, b, a, bit); // bottom-left block
      setModule(grid, a, b, bit); // top-right block
    }
  }
}

function reserve(grid: Grid, row: number, col: number): void {
  const functionRow = grid.isFunction[row];
  if (functionRow !== undefined && functionRow[col] !== true) {
    setModule(grid, row, col, false);
  }
}

/** The standard zigzag: bottom-right upward, column pairs, skipping column 6. */
function placeData(grid: Grid, codewords: readonly number[]): void {
  const size = grid.size;
  const bits: number[] = [];
  for (const byte of codewords) {
    for (let i = 7; i >= 0; i -= 1) bits.push((byte >> i) & 1);
  }
  let bitIndex = 0;
  let upward = true;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5; // the timing column is skipped entirely
    for (let vert = 0; vert < size; vert += 1) {
      const row = upward ? size - 1 - vert : vert;
      for (const col of [right, right - 1]) {
        if (grid.isFunction[row]?.[col] === true) continue;
        const moduleRow = grid.modules[row];
        if (moduleRow === undefined) continue;
        moduleRow[col] = (bits[bitIndex] ?? 0) === 1; // remainder bits are 0
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
}

function maskPredicate(pattern: number, row: number, col: number): boolean {
  switch (pattern) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    case 7:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      throw new RangeError(`Unknown mask pattern ${pattern}`);
  }
}

function applyMask(grid: Grid, pattern: number): void {
  for (let row = 0; row < grid.size; row += 1) {
    for (let col = 0; col < grid.size; col += 1) {
      if (grid.isFunction[row]?.[col] === true) continue;
      const moduleRow = grid.modules[row];
      if (moduleRow === undefined) continue;
      if (maskPredicate(pattern, row, col)) moduleRow[col] = !moduleRow[col];
    }
  }
}

function placeFormatInfo(grid: Grid, maskPattern: number): void {
  const size = grid.size;
  const bits = formatInfoBits(maskPattern);
  const bit = (i: number): boolean => ((bits >> i) & 1) === 1;

  // Copy 1, around the top-left finder (bit 14 first).
  const coords1: readonly (readonly [number, number])[] = [
    [8, 0],
    [8, 1],
    [8, 2],
    [8, 3],
    [8, 4],
    [8, 5],
    [8, 7],
    [8, 8],
    [7, 8],
    [5, 8],
    [4, 8],
    [3, 8],
    [2, 8],
    [1, 8],
    [0, 8],
  ];
  coords1.forEach(([row, col], index) => {
    const moduleRow = grid.modules[row];
    if (moduleRow !== undefined) moduleRow[col] = bit(14 - index);
  });

  // Copy 2: down the left of the bottom-left finder, then right of top-right.
  for (let i = 0; i < 7; i += 1) {
    const moduleRow = grid.modules[size - 1 - i];
    if (moduleRow !== undefined) moduleRow[8] = bit(14 - i);
  }
  for (let i = 7; i < 15; i += 1) {
    const moduleRow = grid.modules[8];
    if (moduleRow !== undefined) moduleRow[size - 15 + i] = bit(14 - i);
  }
}

// ---------------------------------------------------------------------------
// Mask selection penalties (section 8.8.2)
// ---------------------------------------------------------------------------

function penalty(grid: Grid): number {
  const size = grid.size;
  const at = (row: number, col: number): boolean => grid.modules[row]?.[col] === true;
  let score = 0;

  // N1: runs of five or more same-colour modules.
  for (let row = 0; row < size; row += 1) {
    let runColour = at(row, 0);
    let runLength = 1;
    for (let col = 1; col < size; col += 1) {
      if (at(row, col) === runColour) runLength += 1;
      else {
        if (runLength >= 5) score += 3 + (runLength - 5);
        runColour = at(row, col);
        runLength = 1;
      }
    }
    if (runLength >= 5) score += 3 + (runLength - 5);
  }
  for (let col = 0; col < size; col += 1) {
    let runColour = at(0, col);
    let runLength = 1;
    for (let row = 1; row < size; row += 1) {
      if (at(row, col) === runColour) runLength += 1;
      else {
        if (runLength >= 5) score += 3 + (runLength - 5);
        runColour = at(row, col);
        runLength = 1;
      }
    }
    if (runLength >= 5) score += 3 + (runLength - 5);
  }

  // N2: 2x2 blocks of one colour.
  for (let row = 0; row < size - 1; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      const colour = at(row, col);
      if (
        at(row, col + 1) === colour &&
        at(row + 1, col) === colour &&
        at(row + 1, col + 1) === colour
      ) {
        score += 3;
      }
    }
  }

  // N3: finder-like pattern 1011101 with 0000 on either side.
  const pattern1 = [true, false, true, true, true, false, true, false, false, false, false];
  const pattern2 = [false, false, false, false, true, false, true, true, true, false, true];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col <= size - 11; col += 1) {
      let match1 = true;
      let match2 = true;
      for (let i = 0; i < 11; i += 1) {
        if (at(row, col + i) !== pattern1[i]) match1 = false;
        if (at(row, col + i) !== pattern2[i]) match2 = false;
      }
      if (match1) score += 40;
      if (match2) score += 40;
    }
  }
  for (let col = 0; col < size; col += 1) {
    for (let row = 0; row <= size - 11; row += 1) {
      let match1 = true;
      let match2 = true;
      for (let i = 0; i < 11; i += 1) {
        if (at(row + i, col) !== pattern1[i]) match1 = false;
        if (at(row + i, col) !== pattern2[i]) match2 = false;
      }
      if (match1) score += 40;
      if (match2) score += 40;
    }
  }

  // N4: dark-module proportion.
  let dark = 0;
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) if (at(row, col)) dark += 1;
  }
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;
  return score;
}

// ---------------------------------------------------------------------------
// encodeQr
// ---------------------------------------------------------------------------

/** Smallest version 1..10 that fits `byteLength` bytes at EC M. */
export function smallestVersionFor(byteLength: number): number {
  for (let version = 1; version <= 10; version += 1) {
    if (byteCapacity(version) >= byteLength) return version;
  }
  throw new RangeError(
    `Payload of ${byteLength} bytes exceeds the version-10 EC-M capacity ` +
      `(${byteCapacity(10)} bytes)`,
  );
}

export function encodeQr(payload: string | Uint8Array): QrMatrix {
  const bytes = typeof payload === 'string' ? utf8Bytes(payload) : payload;
  const version = smallestVersionFor(bytes.length);
  const size = 17 + version * 4;
  const codewords = interleave(dataCodewords(bytes, version), version);

  let best: { grid: Grid; mask: number; score: number } | null = null;
  for (let mask = 0; mask < 8; mask += 1) {
    const grid = emptyGrid(size);
    placeFunctionPatterns(grid, version);
    placeData(grid, codewords);
    applyMask(grid, mask);
    placeFormatInfo(grid, mask);
    const score = penalty(grid);
    if (best === null || score < best.score) best = { grid, mask, score };
  }
  if (best === null) throw new Error('unreachable: no mask evaluated');

  return {
    version,
    size,
    modules: best.grid.modules,
    maskPattern: best.mask,
  };
}
